import { useState } from 'react';
import { useIntl } from 'react-intl';
import { FlagIcon } from 'sonata-platform-components';
import { ButtonUI } from 'sonata-react-components';

import ClearAllButton from './ClearAllButton';
import FilterSection from './FilterSection';
import FilterSelectors from './FilterSelectors';
import LocationFilterSection from './LocationFilterSection';

import type { FilterPanelConfig } from './filters.types';

type Props = {
  panel: FilterPanelConfig;
};

type SectionId = 'location' | 'attributes';

function FilterPanel({ panel: { attributes, location } }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [openSection, setOpenSection] = useState<SectionId | null>('location');
  const toggle = (section: SectionId) => setOpenSection(current => (current === section ? null : section));

  const clearAll = () => {
    location.onClear();
    attributes.onClear();
  };

  return (
    <>
      <div className="filters__header">
        <div className="filters__title">
          {formatMessage({ defaultMessage: 'Filter by:', id: 'multiFlightListsMap.filters.title' })}
        </div>
        <ClearAllButton disabled={!location.hasActive && !attributes.hasActive} onClear={clearAll} />
      </div>
      <FilterSection
        icon="fa-regular fa-map"
        id="filters-location"
        isOpen={openSection === 'location'}
        onToggle={() => toggle('location')}
        summary={
          location.country && (
            <span className="filters__summary">
              {formatMessage({ defaultMessage: 'Country:', id: 'multiFlightListsMap.filters.country.summary' })}
              <span className="filters__flag">
                <FlagIcon country={location.country.code} id="filters-location-flag" shape="circle" />
              </span>
              {location.country.name}
            </span>
          )
        }
        title={formatMessage({ defaultMessage: 'Location', id: 'multiFlightListsMap.filters.location.title' })}
      >
        <LocationFilterSection location={location} />
      </FilterSection>
      <FilterSection
        icon={attributes.icon}
        id="filters-attributes"
        isOpen={openSection === 'attributes'}
        onToggle={() => toggle('attributes')}
        title={attributes.title}
      >
        <FilterSelectors filters={attributes.filters} />
        <div className="filters__clearSection">
          <ButtonUI
            disabled={!attributes.hasActive}
            icon="fa-solid fa-trash"
            onClick={attributes.onClear}
            shape="link"
            size="ultraSmall"
            text={formatMessage({
              defaultMessage: 'Clear attributes',
              id: 'multiFlightListsMap.filters.clearAttributes'
            })}
            type="secondary"
          />
        </div>
      </FilterSection>
    </>
  );
}

export default FilterPanel;
